/**
 * Overview tab of service plans screen - displaying the list of service plan account has set up
 */
import { FilterOption, ManageableItemsList } from '@payaca/ambrosia';
import moment from 'moment-timezone';
import qs from 'qs';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import CustomerFilterDropdown from './CustomerFilterDropdown';
import {
  ServicePeriodCommitmentsCell,
  ServicePeriodPlanNameCell,
  ServicePeriodStatusCell,
} from './ServicePeriodTableCells';

import {
  DateFormats,
  getInternationalMomentDateFormatByRegion,
} from '@payaca/helpers/internationalHelper';
import * as servicePlansActions from '@payaca/store/servicePlans/servicePlansActions';

import { useSelector } from '../../../api/state';
import { useAccount } from '@/api/rest/users/useAccount';
import { useRegion } from '@/api/rest/users/useRegion';

import { GetServicePeriodsRequestData } from '@payaca/store/servicePlans/servicePlansTypes';
import {
  PublicHydratedServicePlanPeriod,
  ServicePlanPeriod,
} from '@payaca/types/service-plans';

const PAGE_SIZE = 20;

type ServicePeriodsFilters = Omit<
  GetServicePeriodsRequestData,
  'offset' | 'limit'
>;

const servicePeriodStatusOptions: {
  value: ServicePlanPeriod['status'];
  label: string;
}[] = [
  { value: 'unarranged', label: 'Unarranged' },
  { value: 'arranged', label: 'Arranged' },
  { value: 'fulfilled', label: 'Complete' },
];

const whenOptions: {
  value: NonNullable<ServicePeriodsFilters['when']>[number];
  label: string;
}[] = [
  { value: 'past', label: 'Past' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'current', label: 'Current' },
];

const ServicePlansServicePeriods = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const account = useAccount();
  const region = useRegion();

  // servicePlanPublicIds in query to filter by plan
  const servicePlanPublicIds = useMemo(() => {
    const query = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });
    const planIds = query?.servicePlanPublicIds as string;
    return planIds ? planIds.split(',') : [];
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: location.
  }, [location]);
  const customerId = useMemo(() => {
    const query = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });
    if (query?.customerId) {
      return Number(query?.customerId);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: location.
  }, [location]);

  const [filters, setFilters] = useState<ServicePeriodsFilters>({
    servicePlanPublicIds,
    statuses: ['unarranged', 'arranged'],
    when: [],
    customerId: customerId,
  });
  const [pageNumber, setPageNumber] = useState(1);

  // Any filter change resets back to the first page so we don't land on a page
  // that no longer exists for the new result set.
  const updateFilters = (update: Partial<ServicePeriodsFilters>) => {
    setFilters((previous) => ({ ...previous, ...update }));
    setPageNumber(1);
  };

  const listedServicePlans = useSelector(
    (state) => state.servicePlans.listedServicePlans
  );

  const listedServicePlanPeriodsPage = useSelector(
    (state) => state.servicePlans.listedServicePlanPeriodsPage
  );

  useEffect(() => {
    dispatch(
      servicePlansActions.getServicePeriods.request({
        ...filters,
        offset: (pageNumber - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [filters, pageNumber]);

  const isGettingServicePeriods = useSelector(
    (state) => state.servicePlans.isGettingServicePeriods
  );

  const servicePlanOptions = useMemo(() => {
    return (
      listedServicePlans?.map((listedServicePlan) => ({
        value: listedServicePlan.publicId,
        label: listedServicePlan.name,
      })) || []
    );
  }, [listedServicePlans]);

  const filterOptions: FilterOption[] = useMemo(
    () => [
      {
        title: 'Plan',
        name: 'servicePlanPublicIds',
        type: 'checkbox',
        options: servicePlanOptions,
      },
      {
        title: 'Status',
        name: 'statuses',
        type: 'checkbox',
        options: servicePeriodStatusOptions,
      },
      {
        title: 'When',
        name: 'when',
        type: 'checkbox',
        options: whenOptions,
      },
    ],
    [servicePlanOptions]
  );

  const hasFiltersApplied = useMemo(() => {
    // An empty or fully-selected status list means "all statuses" (the request
    // omits it), so only a partial selection counts as a filter.
    const statusesFilterApplied =
      filters.statuses.length > 0 &&
      filters.statuses.length < servicePeriodStatusOptions.length;

    return Boolean(
      filters.servicePlanPublicIds?.length ||
      filters.customerId ||
      filters.when?.length ||
      statusesFilterApplied
    );
  }, [filters]);

  const dateFormat = useMemo(
    () => getInternationalMomentDateFormatByRegion(DateFormats.MID, region),
    [region]
  );

  const servicePlanPeriods = listedServicePlanPeriodsPage?.items ?? [];

  return (
    <ManageableItemsList>
      <ManageableItemsList.ActionBar>
        <CustomerFilterDropdown
          selectedCustomerId={filters.customerId || undefined}
          onChange={(customerId) => {
            updateFilters({ customerId });
          }}
        />
        <ManageableItemsList.ActionBar.AdvancedFilter
          options={filterOptions}
          appliedFilters={{
            servicePlanPublicIds: filters.servicePlanPublicIds || [],
            statuses: filters.statuses || [],
            when: filters.when || [],
          }}
          onApplyFilters={(appliedFilters) => {
            updateFilters({
              servicePlanPublicIds:
                (appliedFilters?.servicePlanPublicIds as string[]) || [],
              statuses:
                (appliedFilters?.statuses as ServicePlanPeriod['status'][]) ||
                [],
              when:
                (appliedFilters?.when as ServicePeriodsFilters['when']) || [],
            });
          }}
        />
      </ManageableItemsList.ActionBar>
      <ManageableItemsList.Table
        items={servicePlanPeriods}
        uniqueKey="publicId"
        isLoading={isGettingServicePeriods}
        emptyText={
          hasFiltersApplied ? 'No results found' : 'No service periods found'
        }
        onClickRow={(row) =>
          history.push(
            row.projectPublicId
              ? `/projects/${row.projectPublicId}/commitments`
              : `/service-plans/service-period/${row.publicId}`
          )
        }
      >
        <ManageableItemsList.Table.Column
          header="Plan"
          field="servicePlanPublicId"
          render={(servicePlanPublicId) => (
            <ServicePeriodPlanNameCell
              servicePlanPublicId={servicePlanPublicId}
            />
          )}
        />
        <ManageableItemsList.Table.Column
          header="Customer"
          field="customerName"
          render={(customerName) => customerName || '-'}
        />
        <ManageableItemsList.Table.Column<
          PublicHydratedServicePlanPeriod,
          'startsAt'
        >
          header="Start"
          field="startsAt"
          render={(startsAt, row) => {
            const isCurrent =
              moment(row.startsAt).isBefore(moment.now()) &&
              moment(row.endsAt).isAfter(moment.now());
            return (
              <span
                className={isCurrent ? 'font-medium text-green-600' : undefined}
              >
                {moment(startsAt).format(dateFormat)}
              </span>
            );
          }}
        />
        <ManageableItemsList.Table.Column<
          PublicHydratedServicePlanPeriod,
          'endsAt'
        >
          header="End"
          field="endsAt"
          render={(endsAt, row) => {
            const isOverdue =
              moment(row.endsAt).isBefore(moment.now()) &&
              row.status !== 'fulfilled';
            return (
              <span
                className={isOverdue ? 'font-medium text-red-600' : undefined}
              >
                {moment(endsAt).format(dateFormat)}
              </span>
            );
          }}
        />
        <ManageableItemsList.Table.Column
          header="Commitments"
          field="commitments"
          render={(commitments) => (
            <ServicePeriodCommitmentsCell commitments={commitments} />
          )}
        />
        <ManageableItemsList.Table.Column<
          PublicHydratedServicePlanPeriod,
          'status'
        >
          header="Status"
          field="status"
          render={(_status, row) => (
            <ServicePeriodStatusCell servicePlanPeriod={row} />
          )}
        />
      </ManageableItemsList.Table>
      {/* Render whenever there are matches, even if the current page is empty
          (e.g. records changed since the page loaded), so the user can always
          navigate back to a populated page. */}
      {!!listedServicePlanPeriodsPage?.totalItemCount && (
        <ManageableItemsList.PaginationBar
          type="custom"
          pageSize={listedServicePlanPeriodsPage.pageSize}
          currentPage={listedServicePlanPeriodsPage.pageNumber}
          totalItems={listedServicePlanPeriodsPage.totalItemCount}
          onPageChange={setPageNumber}
        />
      )}
    </ManageableItemsList>
  );
};

export default ServicePlansServicePeriods;
